import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import {
  selectExtractInvoiceDataLoading,
  selectInvoiceData,
  selectInvoiceError,
} from 'store/invoice/invoice.selector';
import { selectModalInvoiceState } from 'store/modal_invoice/modal_invoice.selector';
import { setModalInvoiceState } from 'store/modal_invoice/modal_invoice.slice';
import { resetInvoiceUpload } from 'store/invoice/invoice.slice';

import { extractInvoiceData } from 'store/invoice/invoice.action';
import { getConsumerUnit } from 'store/consumer_unit/consumer_unit.action';

import * as selectUser from 'store/user/user.selector';

import { alert } from 'lib/alert';
import { getInvoiceUploadErrorMessage } from 'utils/invoiceUploadError';
import { documentNumberMask } from 'utils/masks';
import { canBulkUpload } from 'utils/planRules';
import { requestUtilitySupportService } from 'services/utility_support.service';

import { LoadingInvoice } from '../loading-invoice/loading-invoice.component';

import {
  DropZone,
  DropZoneLabel,
  DropZoneSeparator,
  DropZoneSubLabel,
  HelpText,
  InfoBox,
  LinkButton,
  PasswordInput,
  PasswordPromptActions,
  PasswordPromptError,
  PasswordPromptForm,
  PasswordPromptIcon,
  PasswordPromptText,
  PasswordPromptTitle,
  UploadInvoiceContent,
} from './select-invoice.styles';
import { Button } from 'ui/button/button.ui';

// Toast do erro 2004 (distribuidora não suportada) com botão de aviso
const UnsupportedUtilityToast = ({
  utilityName,
}: {
  utilityName?: string | null;
}) => {
  const [sent, setSent] = useState(false);

  const handleNotifyMe = async () => {
    try {
      await requestUtilitySupportService(utilityName ?? 'Não identificada');
      setSent(true);
    } catch {
      alert.error('Não foi possível registrar seu pedido. Tente novamente.');
    }
  };

  return (
    <div>
      <p>
        {utilityName
          ? `A distribuidora ${utilityName} ainda não é suportada pelo EnergyPass.`
          : 'A distribuidora desta fatura ainda não é suportada pelo EnergyPass.'}{' '}
        Nossa equipe já foi notificada.
      </p>
      {sent ? (
        <p>
          <strong>Pronto! Você será avisado quando houver suporte.</strong>
        </p>
      ) : (
        <button
          type="button"
          onClick={handleNotifyMe}
          style={{
            marginTop: 8,
            padding: '6px 12px',
            border: '1px solid #fff',
            borderRadius: 4,
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Me avise quando houver suporte
        </button>
      )}
    </div>
  );
};

export const SelectInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  // Só reage ao resultado (prévia ou erro) de um upload feito NESTA sessão do
  // modal. Sem isso, ao reabrir o modal o erro do upload anterior (ainda no
  // Redux) disparava o toast e fechava o modal antes de mostrar a dropzone.
  const [submitted, setSubmitted] = useState(false);
  // PDF protegido por senha (código 1102): em vez de fechar o modal e mandar o
  // usuário para o tutorial, pedimos o CPF/CNPJ do titular aqui dentro e
  // reenviamos o MESMO arquivo com o documento. `wrong` = o documento anterior
  // não abriu; `attempts` conta as tentativas para oferecer o tutorial ao fim.
  const [passwordPrompt, setPasswordPrompt] = useState<{
    wrong: boolean;
  } | null>(null);
  const [documentValue, setDocumentValue] = useState('');
  const [attempts, setAttempts] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  // Guarda o último PDF enviado para permitir o reenvio com o documento digitado
  // (o `file` de estado é zerado após o dispatch).
  const lastFileRef = useRef<File | null>(null);

  const extractLoading = useAppSelector(selectExtractInvoiceDataLoading);
  const invoiceData = useAppSelector(selectInvoiceData);
  const modalState = useAppSelector(selectModalInvoiceState);
  const currentUser = useAppSelector(selectUser.currentUser);
  // Upload em massa é exclusivo dos planos pagos (Starter/Business/Enterprise)
  const allowsBulk = canBulkUpload(currentUser?.plan?.name ?? 'Free');
  const invoiceError = useAppSelector(selectInvoiceError);

  // Ao abrir a tela de upload, descarta o resultado do upload anterior para
  // começar limpo (o modal apenas esconde, não desmonta o estado global).
  useEffect(() => {
    dispatch(resetInvoiceUpload());
  }, [dispatch]);

  useEffect(() => {
    if (submitted && !extractLoading && modalState === 'select_invoice') {
      if (invoiceData && !invoiceError) {
        if (!invoiceData.consumerUnitExists) {
          dispatch(setModalInvoiceState({ modalState: 'hidden' }));
          navigate('/unidades-consumidoras/adicionar');
        } else {
          if (invoiceData.consumerUnitId) {
            dispatch(
              getConsumerUnit({ consumerUnitId: invoiceData.consumerUnitId })
            );
          }
          dispatch(setModalInvoiceState({ modalState: 'save_invoice' }));
        }
      } else if (!invoiceData && invoiceError) {
        if (invoiceError.type === 'message') {
          // PDF protegido (1102): mantém o modal aberto e pede o CPF/CNPJ inline.
          if (invoiceError.message === '1102') {
            setPasswordPrompt({
              wrong: Boolean(invoiceError.data?.documentAttempted),
            });
            return;
          }
          dispatch(setModalInvoiceState({ modalState: 'hidden' }));
          const plainMessage = getInvoiceUploadErrorMessage(
            invoiceError.message
          );

          if (plainMessage !== null) {
            alert.error(plainMessage);
          } else if (invoiceError.message === '2002') {
            alert.error(
              <p>
                O arquivo enviado é uma imagem escaneada. É preciso enviar a
                fatura em PDF texto, baixada do site ou app da sua
                distribuidora. Clique{' '}
                <a
                  href={`${process.env.REACT_APP_URL}/ajuda/como-obter-fatura-em-pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  aqui
                </a>{' '}
                para aprender como baixar o PDF correto.
              </p>
            );
          } else if (invoiceError.message === '2004') {
            alert.error(
              <UnsupportedUtilityToast
                utilityName={invoiceError.data?.utilityName}
              />
            );
          } else if (invoiceError.message === '1101') {
            alert.error(
              <p>
                Não foi possível desbloquear a senha do arquivo. Você pode
                conferir se o CPF/CNPJ desta unidade consumidora esta correto?
                Caso essa conta seja de uma UC nova, clique{' '}
                <a
                  href={`${process.env.REACT_APP_URL}/ajuda/remover-senha-arquivo-pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  aqui
                </a>{' '}
                para aprender a retirar a senha.
              </p>
            );
          } else if (invoiceError.message === '1103') {
            alert.error(
              <p>
                Não foi possível desbloquear a senha do arquivo. Clique{' '}
                <a
                  href={`${process.env.REACT_APP_URL}/ajuda/remover-senha-arquivo-pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  aqui
                </a>{' '}
                para descobrir como desbloquear.
              </p>
            );
          }
        }
      }
    }
  }, [
    submitted,
    extractLoading,
    modalState,
    invoiceData,
    invoiceError,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    if (file) {
      setSubmitted(true);
      lastFileRef.current = file;
      dispatch(extractInvoiceData({ file }));
      setFile(null);
    }
  }, [file]);

  // Reenvia o mesmo PDF com o CPF/CNPJ digitado (retry do prompt de senha).
  const handleSubmitDocument = (e: React.FormEvent) => {
    e.preventDefault();
    const digits = documentValue.replace(/\D/g, '');
    if (digits.length < 4 || !lastFileRef.current) return;

    setAttempts((prev) => prev + 1);
    setPasswordPrompt(null);
    setSubmitted(true);
    dispatch(
      extractInvoiceData({ file: lastFileRef.current, documentNumber: digits })
    );
  };

  const handleCancelPasswordPrompt = () => {
    setPasswordPrompt(null);
    setDocumentValue('');
    setAttempts(0);
    lastFileRef.current = null;
  };

  const validateAndSubmit = useCallback((selected: File) => {
    if (selected.type !== 'application/pdf') {
      alert.error(
        'O arquivo enviado não está no padrão correto. Por favor, envie sua fatura de energia em formato PDF.'
      );
      return;
    }
    setFile(selected);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const selected = e.target.files?.item(0);
    if (selected) validateAndSubmit(selected);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDropZoneClick = () => {
    inputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    // Vários arquivos de uma vez → direciona para a página de upload em lote
    if (e.dataTransfer.files.length > 1) {
      dispatch(setModalInvoiceState({ modalState: 'hidden' }));
      navigate('/upload-faturas');
      return;
    }

    const dropped = e.dataTransfer.files.item(0);
    if (dropped) validateAndSubmit(dropped);
  };

  if (extractLoading) {
    return <LoadingInvoice />;
  }

  if (passwordPrompt) {
    const digits = documentValue.replace(/\D/g, '');
    return (
      <UploadInvoiceContent>
        <PasswordPromptForm onSubmit={handleSubmitDocument}>
          <PasswordPromptIcon>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </PasswordPromptIcon>

          <PasswordPromptTitle>
            Esta fatura está protegida por senha
          </PasswordPromptTitle>

          <PasswordPromptText>
            Para abrir a conta automaticamente, confirme o{' '}
            <strong>CPF ou CNPJ do titular</strong> da conta de energia. Usamos
            apenas os primeiros dígitos para desbloquear o PDF — o mesmo que a
            distribuidora pede.
          </PasswordPromptText>

          <PasswordInput
            type="text"
            inputMode="numeric"
            autoFocus
            placeholder="CPF ou CNPJ do titular"
            value={documentValue}
            $hasError={passwordPrompt.wrong}
            onChange={(e) =>
              setDocumentValue(documentNumberMask(e.target.value))
            }
          />

          {passwordPrompt.wrong && (
            <PasswordPromptError>
              Não conseguimos abrir a fatura com esse documento. Confira se é o
              CPF/CNPJ que aparece na conta de energia.
            </PasswordPromptError>
          )}

          <PasswordPromptActions>
            <Button type="submit" disabled={digits.length < 4}>
              Desbloquear fatura
            </Button>
          </PasswordPromptActions>

          {attempts >= 2 && (
            <HelpText>
              Ainda não abriu? Você pode{' '}
              <a
                href={`${process.env.REACT_APP_URL}/ajuda/remover-senha-arquivo-pdf`}
                target="_blank"
                rel="noreferrer"
              >
                remover a senha do PDF
              </a>{' '}
              e enviar novamente.
            </HelpText>
          )}

          <LinkButton type="button" onClick={handleCancelPasswordPrompt}>
            Enviar outra fatura
          </LinkButton>
        </PasswordPromptForm>
      </UploadInvoiceContent>
    );
  }

  return (
    <UploadInvoiceContent>
      <DropZone
        $isDragging={isDragging}
        onClick={handleDropZoneClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          onChange={handleFileInputChange}
          hidden
          accept="application/pdf"
        />

        <svg
          className="upload-icon"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polyline points="16 16 12 12 8 16" />
          <line x1="12" y1="12" x2="12" y2="21" />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>

        <DropZoneLabel>
          {isDragging ? 'Solte o arquivo aqui' : 'Arraste sua fatura aqui'}
        </DropZoneLabel>

        <DropZoneSeparator>ou</DropZoneSeparator>

        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Selecionar arquivo
        </Button>

        <DropZoneSubLabel>Somente arquivos PDF</DropZoneSubLabel>
      </DropZone>

      <InfoBox>
        <svg
          width="32"
          height="32"
          viewBox="0 0 64 65"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M32 60.5C16.536 60.5 4 47.964 4 32.5C4 17.036 16.536 4.5 32 4.5C47.464 4.5 60 17.036 60 32.5C60 47.964 47.464 60.5 32 60.5ZM32 64.5C49.6731 64.5 64 50.1731 64 32.5C64 14.8269 49.6731 0.5 32 0.5C14.3269 0.5 0 14.8269 0 32.5C0 50.1731 14.3269 64.5 32 64.5Z"
            fill="#999999"
          />
          <path
            d="M28.0062 44.5C28.0062 42.2909 29.797 40.5 32.0062 40.5C34.2153 40.5 36.0062 42.2909 36.0062 44.5C36.0062 46.7091 34.2153 48.5 32.0062 48.5C29.797 48.5 28.0062 46.7091 28.0062 44.5Z"
            fill="#999999"
          />
          <path
            d="M28.398 20.4802C28.1849 18.3491 29.8584 16.5 32 16.5C34.1416 16.5 35.8151 18.3491 35.602 20.4801L34.199 34.5099C34.086 35.6397 33.1354 36.5 32 36.5C30.8646 36.5 29.914 35.6397 29.801 34.5099L28.398 20.4802Z"
            fill="#999999"
          />
        </svg>
        <div>
          <h3>Importante</h3>
          <p>
            Se a unidade já existir, o sistema irá atualizar as informações com
            os dados da conta.
          </p>
        </div>
      </InfoBox>

      {allowsBulk && (
        <HelpText>
          Tem várias faturas?{' '}
          <Link
            to="/upload-faturas"
            onClick={() =>
              dispatch(setModalInvoiceState({ modalState: 'hidden' }))
            }
          >
            Envie todas de uma vez
          </Link>
          .
        </HelpText>
      )}

      <HelpText>
        Não quer baixar o PDF todo mês?{' '}
        <Link
          to="/integracoes"
          onClick={() =>
            dispatch(setModalInvoiceState({ modalState: 'hidden' }))
          }
        >
          Receba suas faturas automaticamente por e-mail
        </Link>
        .
      </HelpText>

      <HelpText>
        <Link
          to="/ajuda/como-obter-fatura-em-pdf"
          onClick={() =>
            dispatch(setModalInvoiceState({ modalState: 'hidden' }))
          }
        >
          Visite nossa central de ajuda
        </Link>{' '}
        para entender melhor sobre a importação de contas dentro do EnergyPass.
      </HelpText>
    </UploadInvoiceContent>
  );
};
